import { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { applyTheme } from '../utils/theme';
import { getContrastRatio } from '../utils/contrast';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Upload, X, Check } from 'lucide-react';

const FONT_OPTIONS = [
  { name: 'Outfit', category: 'Geometric Sans' },
  { name: 'Inter', category: 'Neutral Sans' },
  { name: 'DM Sans', category: 'Geometric Sans' },
  { name: 'Plus Jakarta Sans', category: 'Modern Sans' },
  { name: 'Work Sans', category: 'Grotesque Sans' },
  { name: 'Poppins', category: 'Geometric Sans' },
  { name: 'Nunito', category: 'Rounded Sans' },
  { name: 'Lato', category: 'Humanist Sans' },
  { name: 'Open Sans', category: 'Humanist Sans' },
  { name: 'Roboto', category: 'Neo-Grotesque' },
  { name: 'Source Sans 3', category: 'Humanist Sans' },
  { name: 'Raleway', category: 'Elegant Sans' },
];

const ColourRow = ({ label, value, onChange }) => {
  const ratio = value ? getContrastRatio(value) : 99;
  return (
    <div className="flex items-center gap-4 py-2.5 border-b border-zinc-100 last:border-0">
      <div className="w-36 text-sm font-medium text-zinc-700">{label}</div>
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} className="w-9 h-9 rounded-sm border border-zinc-200 cursor-pointer p-0.5" />
      <Input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className="font-mono text-xs rounded-sm h-8 w-28" />
      <div className="w-7 h-7 rounded-sm border border-zinc-200" style={{ background: value || '#000' }} />
      {ratio < 4.5 && <span className="text-[10px] font-semibold text-amber-600">Low contrast on white</span>}
    </div>
  );
};

export default function SettingsPage() {
  const { settings, setSettings, fetchSettings } = useSettings();
  const [colours, setColours] = useState({
    hero_colour: settings?.hero_colour || '#1C3557',
    btn_primary_colour: settings?.btn_primary_colour || '#1C3557',
    btn_secondary_colour: settings?.btn_secondary_colour || '#F5F4EF',
    btn_danger_colour: settings?.btn_danger_colour || '#E24B4A',
    print_accent_colour: settings?.print_accent_colour || '#1C3557',
  });
  const [logoPos, setLogoPos] = useState(settings?.logo_position || 'top_left');
  const [selectedFont, setSelectedFont] = useState(settings?.font_family || 'Outfit');
  const [saving, setSaving] = useState(false);
  const [savingFont, setSavingFont] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleSaveColours = async () => {
    setSaving(true);
    try {
      const res = await api.patch('/settings', { ...colours, logo_position: logoPos });
      setSettings(res.data); applyTheme(res.data); toast.success('Settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleResetColours = () => {
    setColours({ hero_colour: '#1C3557', btn_primary_colour: '#1C3557', btn_secondary_colour: '#F5F4EF', btn_danger_colour: '#E24B4A', print_accent_colour: '#1C3557' });
  };

  const handleSaveFont = async () => {
    setSavingFont(true);
    try {
      const res = await api.patch('/settings', { font_family: selectedFont });
      setSettings(res.data); applyTheme(res.data); toast.success('Font updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSavingFont(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchSettings(); toast.success('Logo uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleLogoDelete = async () => {
    try { await api.delete('/settings/logo'); await fetchSettings(); toast.success('Logo removed'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Preload font for preview when hovering/selecting
  const preloadFont = (fontName) => {
    const id = `preview-font-${fontName.replace(/\s+/g, '-')}`;
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
    }
  };

  return (
    <div className="p-6" data-testid="settings-page">
      <p className="text-sm text-zinc-500 mb-6">Configure branding and appearance</p>

      <Tabs defaultValue="branding">
        <TabsList className="bg-zinc-100 rounded-sm h-9 p-0.5 mb-6">
          <TabsTrigger value="branding" className="rounded-sm text-sm font-medium px-4 data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-sm">
            Branding
          </TabsTrigger>
          <TabsTrigger value="colours" className="rounded-sm text-sm font-medium px-4 data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-sm">
            Colours
          </TabsTrigger>
          <TabsTrigger value="font" className="rounded-sm text-sm font-medium px-4 data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-sm" data-testid="font-tab">
            Font
          </TabsTrigger>
        </TabsList>

          <TabsContent value="branding" className="mt-0">
            <h2 className="text-sm font-bold text-zinc-900 mb-3">Logo</h2>
            <Card className="rounded-sm border-zinc-200 shadow-none mb-5">
              <CardContent className="p-5">
                {settings?.logo_url ? (
                  <div className="flex items-center gap-4">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`} alt="Logo" className="h-12 object-contain" />
                    <Button variant="ghost" size="sm" onClick={handleLogoDelete} className="text-red-500 hover:text-red-700 text-xs gap-1 rounded-sm">
                      <X size={12} /> Remove
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-zinc-300 rounded-sm cursor-pointer hover:border-zinc-400 transition-colors">
                    <Upload size={22} className="text-zinc-300 mb-2" />
                    <span className="text-xs text-zinc-400 font-medium">{uploading ? 'Uploading...' : 'Upload PNG or SVG (max 2MB)'}</span>
                    <input type="file" accept=".png,.svg,image/png,image/svg+xml" onChange={handleLogoUpload} className="hidden" />
                  </label>
                )}
              </CardContent>
            </Card>

            <h2 className="text-sm font-bold text-zinc-900 mb-2">Logo position on checklist</h2>
            <div className="flex gap-2 mb-5">
              {['top_left', 'top_centre', 'top_right'].map(pos => (
                <Button key={pos} variant={logoPos === pos ? 'default' : 'outline'} size="sm" onClick={() => setLogoPos(pos)}
                  className={`rounded-sm text-xs ${logoPos === pos ? 'bg-zinc-900 text-white' : ''}`}>
                  {pos.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </Button>
              ))}
            </div>

            <Card className="rounded-sm border-zinc-200 shadow-none">
              <CardContent className="p-5">
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3 block">Print header preview</Label>
                <div className="border border-zinc-200 p-4 rounded-sm" style={{ borderBottom: `2px solid ${colours.print_accent_colour}` }}>
                  <div className={`flex items-center ${logoPos === 'top_centre' ? 'justify-center' : logoPos === 'top_right' ? 'justify-end' : 'justify-start'}`}>
                    {settings?.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`} alt="Logo" className="h-8 object-contain" /> : <div className="w-16 h-8 bg-zinc-100 rounded-sm flex items-center justify-center text-[9px] text-zinc-400 font-semibold">LOGO</div>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="colours" className="mt-0">
            <h2 className="text-sm font-bold text-zinc-900 mb-3">Colour theme</h2>
            <Card className="rounded-sm border-zinc-200 shadow-none mb-4">
              <CardContent className="p-5">
                <ColourRow label="Hero colour" value={colours.hero_colour} onChange={v => setColours({ ...colours, hero_colour: v })} />
                <ColourRow label="Primary button" value={colours.btn_primary_colour} onChange={v => setColours({ ...colours, btn_primary_colour: v })} />
                <ColourRow label="Secondary button" value={colours.btn_secondary_colour} onChange={v => setColours({ ...colours, btn_secondary_colour: v })} />
                <ColourRow label="Danger button" value={colours.btn_danger_colour} onChange={v => setColours({ ...colours, btn_danger_colour: v })} />
                <ColourRow label="Print accent" value={colours.print_accent_colour} onChange={v => setColours({ ...colours, print_accent_colour: v })} />
              </CardContent>
            </Card>
            <div className="flex gap-2">
              <Button onClick={handleSaveColours} disabled={saving} data-testid="save-colours-btn" className="rounded-sm text-sm"
                style={{ background: 'var(--brand-primary, #1C3557)' }}>
                {saving ? 'Saving...' : 'Save colours'}
              </Button>
              <Button variant="outline" onClick={handleResetColours} className="rounded-sm text-sm">Reset to defaults</Button>
            </div>
          </TabsContent>

          <TabsContent value="font" className="mt-0">
            <h2 className="text-sm font-bold text-zinc-900 mb-1">App font</h2>
            <p className="text-xs text-zinc-500 mb-4">Choose a font for the entire application. Changes apply immediately on save.</p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {FONT_OPTIONS.map(f => {
                const isSelected = selectedFont === f.name;
                return (
                  <Card key={f.name}
                    onClick={() => { setSelectedFont(f.name); preloadFont(f.name); }}
                    onMouseEnter={() => preloadFont(f.name)}
                    data-testid={`font-option-${f.name.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`rounded-sm shadow-none cursor-pointer transition-all duration-150 ${
                      isSelected ? 'border-2 border-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                    }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-zinc-500">{f.category}</span>
                        {isSelected && <Check size={14} className="text-zinc-900" />}
                      </div>
                      <div className="text-xl font-semibold text-zinc-900 mb-1" style={{ fontFamily: `'${f.name}', sans-serif` }}>
                        {f.name}
                      </div>
                      <div className="text-sm text-zinc-600" style={{ fontFamily: `'${f.name}', sans-serif` }}>
                        The quick brown fox jumps over the lazy dog
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="rounded-sm border-zinc-200 shadow-none mb-5">
              <CardContent className="p-5">
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3 block">Preview</Label>
                <div style={{ fontFamily: `'${selectedFont}', sans-serif` }}>
                  <div className="text-2xl font-bold text-zinc-900 mb-1">Project Start Checklist</div>
                  <div className="text-sm text-zinc-600 mb-3">This is how your app content will look with <strong>{selectedFont}</strong>.</div>
                  <div className="text-xs text-zinc-400">ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789</div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button onClick={handleSaveFont} disabled={savingFont} data-testid="save-font-btn" className="rounded-sm text-sm"
                style={{ background: 'var(--brand-primary, #1C3557)' }}>
                {savingFont ? 'Saving...' : 'Save font'}
              </Button>
              <Button variant="outline" onClick={() => setSelectedFont('Outfit')} className="rounded-sm text-sm">Reset to default</Button>
            </div>
          </TabsContent>
      </Tabs>
    </div>
  );
}
